/**
 * @file InvoicesSection.tsx
 * @brief Billing → Invoices: every charge with date, item, minutes, amount,
 *        status, and the PDF.
 *
 * @details
 * The unified feed (GET /invoices) scoped to the caller. Amount and status
 * come from shared/billing/moneyStatus (collected vs billed, failed, refunded,
 * part refunded) with Deleted / Disputed markers (18-10-2026).
 *
 * @author DES
 * @date 17-10-2026
 */

import { Download, LoaderCircle } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { SkeletonRows } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/feedback/EmptyState";
import { GridBand, GridCell } from "@/features/analytics/components/GridBlock";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { downloadInvoicePdf, useMyInvoices } from "../api";
import { invoiceItemLabel, invoiceMinutes } from "../billingPeriod";
import { MoneyAmount, MoneyBadges } from "@/shared/billing/MoneyCells";
import { formatDay } from "./format";

const PAGE_SIZE = 20;

export function InvoicesSection() {
  const [page, setPage] = useState(1);
  const list = useMyInvoices({ page, pageSize: PAGE_SIZE });
  const [busyId, setBusyId] = useState<string | null>(null);
  const total = Number(list.data?.total ?? 0);
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rows = list.data?.items ?? [];

  const download = async (id: string) => {
    setBusyId(id);
    try {
      await downloadInvoicePdf(id);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Couldn't download the invoice",
        description: err instanceof ApiClientError ? err.message : "Please try again.",
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <GridBand>
        <h3 className="text-base font-semibold tracking-tight">Invoices and receipts</h3>
        <p className="mt-1 text-sm text-muted-foreground">Every plan payment and pay-as-you-go charge, newest first.</p>
      </GridBand>
      <GridCell padded={false}>
        {list.isLoading ? (
          <SkeletonRows rows={4} twoLine />
        ) : list.isError ? (
          <div className="px-4 py-3 text-sm text-destructive md:px-5">
            Couldn't load your invoices.{" "}
            <button type="button" className="underline" onClick={() => void list.refetch()}>
              Try again
            </button>
          </div>
        ) : rows.length === 0 ? (
          <EmptyState shape="invoice" title="No invoices yet" description="Receipts appear here after your first payment." />
        ) : (
          <>
          {/* Phone width: one stacked row per invoice, amount and status kept
              in view (the table would hide them behind a sideways scroll). */}
          <ul className="divide-y divide-border/60 sm:hidden">
            {rows.map((r) => {
              const minutes = invoiceMinutes(r);
              const cur = r.currency ?? "GBP";
              return (
                <li key={`${r.product}-${r.id}`} className="flex items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{invoiceItemLabel(r.product)}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatDay(r.createdAt) ?? "—"}
                      {minutes != null ? ` · ${minutes} min` : ""}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 text-right">
                    <MoneyAmount row={r} currency={cur} />
                    <MoneyBadges row={r} className="justify-end" />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Download invoice from ${formatDay(r.createdAt) ?? ""}`}
                    disabled={busyId === r.id}
                    onClick={() => void download(r.id)}
                  >
                    {busyId === r.id ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  </Button>
                </li>
              );
            })}
          </ul>
          <div className="hidden overflow-x-auto sm:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-2.5 font-medium md:px-5">Date</th>
                  <th className="px-3 py-2.5 font-medium">Item</th>
                  <th className="px-3 py-2.5 text-right font-medium">Minutes</th>
                  <th className="px-3 py-2.5 text-right font-medium">Amount</th>
                  <th className="px-3 py-2.5 font-medium">Status</th>
                  <th className="px-4 py-2.5 md:px-5">
                    <span className="sr-only">PDF</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {rows.map((r) => {
                  const minutes = invoiceMinutes(r);
                  const cur = r.currency ?? "GBP";
                  return (
                    <tr key={`${r.product}-${r.id}`}>
                      <td className="whitespace-nowrap px-4 py-3 md:px-5">{formatDay(r.createdAt) ?? "—"}</td>
                      <td className="px-3 py-3">{invoiceItemLabel(r.product)}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{minutes ?? "—"}</td>
                      <td className="px-3 py-3 text-right">
                        <MoneyAmount row={r} currency={cur} />
                      </td>
                      <td className="px-3 py-3">
                        <MoneyBadges row={r} />
                      </td>
                      <td className="px-4 py-3 text-right md:px-5">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Download invoice from ${formatDay(r.createdAt) ?? ""}`}
                          disabled={busyId === r.id}
                          onClick={() => void download(r.id)}
                        >
                          {busyId === r.id ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </GridCell>
      {pages > 1 ? (
        <GridBand className="flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            Page {page} of {pages}
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <Button variant="ghost" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        </GridBand>
      ) : null}
    </>
  );
}
